import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Ticket, Send, Check, Navigation, MapPin, CheckCircle
} from "lucide-react";
import { useTickets } from "@/context/TicketContext";
import { Header } from "@/components/Header";
import { TicketFormModal } from "@/components/TicketFormModal";
import { CloseTicketModal } from "@/components/CloseTicketModal";
import { formatDispatchTime } from "@/utils/formatDateTime";

const DispatchedTicketsPage = () => {
  const navigate = useNavigate();
  const { getDispatchedTickets, updateTicket, closeTicket } = useTickets();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  const dispatchedTickets = getDispatchedTickets();

  const handleEditClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowCloseModal(false);
    setSelectedTicket(null);
  };

  const handleUpdateTicket = (formData) => {
    updateTicket(formData.ticketId, formData);
    handleCloseModal();
  };

  const handleCloseTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowCloseModal(true);
  };

  const handleConfirmClose = (ticketId, closingComments) => {
    closeTicket(ticketId, closingComments);
    handleCloseModal();
  };

  const handleAcknowledge = async (ticket) => {
    await updateTicket(ticket.ticketId, {
      status: 'Dispatch Acknowledged',
      acknowledgedAt: new Date().toISOString()
    });
  };

  return (
    <div className="app-container" data-testid="dispatched-tickets-page">
      <Header />

      {/* Dispatched Tickets Content */}
      <main className="main-content">
        <div className="device-page-header">
          <div>
            <h1 className="device-page-title dispatched-title">
              <Send size={24} className="dispatched-icon" />
              Dispatched Tickets
            </h1>
            <p className="device-page-subtitle">{dispatchedTickets.length} dispatched ticket{dispatchedTickets.length !== 1 ? 's' : ''}</p>
          </div>
          <button className="close-btn" onClick={() => navigate('/')} data-testid="close-btn">
            <X size={24} />
          </button>
        </div>

        {dispatchedTickets.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <Send size={48} className="empty-state-icon purple" />
            <h3>No Dispatched Tickets</h3>
            <p>There are no dispatched tickets at this time.</p>
            <button className="btn-back" onClick={() => navigate('/')}>
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div className="tickets-list" data-testid="dispatched-list">
            {dispatchedTickets.map(ticket => (
              <div key={ticket.ticketId} className="ticket-card dispatched-card" data-testid={`dispatched-card-${ticket.ticketId}`}>
                <div className="ticket-card-header dispatched-header">
                  <div className="ticket-id-section">
                    <Ticket size={18} className="ticket-icon" />
                    <span className="ticket-id">{ticket.ticketId}</span>
                    <span className={`ticket-status-badge ${ticket.status.toLowerCase().replace(' ', '-')}`}>{ticket.status}</span>
                    <span className={`ticket-priority-badge ${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
                  </div>
                  <div className="ticket-header-right">
                    <div className="ticket-actions-header">
                      <button 
                        className="ticket-action-btn edit" 
                        data-testid={`edit-btn-${ticket.ticketId}`}
                        onClick={() => handleEditClick(ticket)}
                      >
                        EDIT
                      </button>
                      {ticket.status === 'Dispatched' && (
                        <button 
                          className="ticket-action-btn dispatch"
                          data-testid={`ack-btn-${ticket.ticketId}`}
                          onClick={() => handleAcknowledge(ticket)}
                        >
                          ACKNOWLEDGE
                        </button>
                      )}
                      <button 
                        className="ticket-action-btn close-ticket" 
                        data-testid={`close-btn-${ticket.ticketId}`}
                        onClick={() => handleCloseTicketClick(ticket)}
                      >
                        CLOSE
                      </button>
                    </div>
                    <span className="ticket-date">Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="ticket-card-body">
                  <div className="ticket-info-row">
                    <span className="ticket-label">Subscriber:</span>
                    <span className="ticket-value">{ticket.subscriberName}</span>
                    <span className="ticket-label">Device:</span>
                    <span className="ticket-value">{ticket.deviceId}</span>
                    <span className="ticket-label">Issue:</span>
                    <span className="ticket-value issue">{ticket.issueType}</span>
                  </div>
                  <div className="ticket-info-row">
                    <span className="ticket-label">Location:</span>
                    <span className="ticket-value">{ticket.location}</span>
                  </div>
                  <div className="ticket-info-row">
                    <span className="ticket-label">Assigned To:</span>
                    <span className="ticket-value">{ticket.assignedTo || 'Unassigned'}</span>
                    <span className="ticket-label">Due:</span>
                    <span className="ticket-value">{ticket.dueDate || 'Not set'}</span>
                  </div>
                  {ticket.dispatchedAt && (
                    <div className="ticket-dispatched-info" data-testid={`dispatched-info-${ticket.ticketId}`}>
                      <Send size={16} className="dispatch-icon" />
                      <span className="dispatch-label">Dispatched:</span>
                      <span className="dispatch-time">{formatDispatchTime(ticket.dispatchedAt)}</span>
                    </div>
                  )}
                  {ticket.acknowledgedAt && (
                    <div className="ticket-dispatched-info" data-testid={`acknowledged-info-${ticket.ticketId}`} style={{borderColor: 'rgba(0, 240, 255, 0.3)', background: 'rgba(0, 240, 255, 0.05)'}}>
                      <Check size={16} className="dispatch-icon" style={{color: '#00f0ff'}} />
                      <span className="dispatch-label" style={{color: '#00f0ff'}}>Acknowledged:</span>
                      <span className="dispatch-time">{formatDispatchTime(ticket.acknowledgedAt)}</span>
                    </div>
                  )}
                  {ticket.enRouteAt && (
                    <div className="ticket-dispatched-info" style={{borderColor: 'rgba(255, 165, 0, 0.3)', background: 'rgba(255, 165, 0, 0.05)'}}>
                      <Navigation size={16} className="dispatch-icon" style={{color: '#ffa500'}} />
                      <span className="dispatch-label" style={{color: '#ffa500'}}>En Route:</span>
                      <span className="dispatch-time">{formatDispatchTime(ticket.enRouteAt)}</span>
                    </div>
                  )}
                  {ticket.onSiteAt && (
                    <div className="ticket-dispatched-info" style={{borderColor: 'rgba(0, 255, 136, 0.3)', background: 'rgba(0, 255, 136, 0.05)'}}>
                      <MapPin size={16} className="dispatch-icon" style={{color: '#00ff88'}} />
                      <span className="dispatch-label" style={{color: '#00ff88'}}>On Site:</span>
                      <span className="dispatch-time">{formatDispatchTime(ticket.onSiteAt)}</span>
                    </div>
                  )}
                  {ticket.workCompleteAt && (
                    <div className="ticket-dispatched-info" style={{borderColor: 'rgba(0, 200, 255, 0.3)', background: 'rgba(0, 200, 255, 0.05)'}}>
                      <CheckCircle size={16} className="dispatch-icon" style={{color: '#00c8ff'}} />
                      <span className="dispatch-label" style={{color: '#00c8ff'}}>Work Complete:</span>
                      <span className="dispatch-time">{formatDispatchTime(ticket.workCompleteAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Edit Ticket Modal */}
      {showEditModal && selectedTicket && (
        <TicketFormModal 
          editTicket={selectedTicket}
          onClose={handleCloseModal}
          onSubmit={handleUpdateTicket}
        />
      )}

      {/* Close Ticket Modal */}
      {showCloseModal && selectedTicket && (
        <CloseTicketModal 
          ticket={selectedTicket}
          onClose={handleCloseModal}
          onConfirm={handleConfirmClose}
        />
      )}
    </div>
  );
};

export default DispatchedTicketsPage;
